import { useAuthStore } from '@client/stores/AuthStore';
import type { AuthStoreOrg } from '@client/stores/AuthStore/types';
import type { RouterError } from '@client/trpc/client';
import { isProtectedRoute, sessionStopsQueryRetry } from '@client/trpc/sessionHandling';

/** Client copy of the server's `MY_ITEMS_V2_FLAG_KEY`, which gates `dashboard.getMyItemsExtras`. */
export const MY_ITEMS_V2_FLAG_KEY = 'enable-my-items-v2';

/**
 * Retry policy for `dashboard.getMyItemsExtras`, carried by both the route prefetch and Home's
 * observer: an observer that mounts while the prefetch is in flight joins the prefetch's retryer,
 * which keeps the policy it started with. Applies the global session stop, then retries anything
 * but the flag-off `NOT_FOUND` up to three times.
 */
export function shouldRetryMyItemsExtras(failureCount: number, error: Pick<RouterError, 'data'>): boolean {
    if (
        sessionStopsQueryRetry({
            authTokenExpired: useAuthStore.getState().authTokenExpired,
            isProtectedRoute: isProtectedRoute(window.location.pathname),
        })
    ) {
        return false;
    }
    return error.data?.code !== 'NOT_FOUND' && failureCount < 3;
}

export function shouldPrefetchHomeDashboardCounts({
    hasValidToken,
    isTrainingManagerOnly,
    hydratedOrg,
    routeOrgId,
    navigationCause,
}: {
    hasValidToken: boolean;
    isTrainingManagerOnly: boolean;
    hydratedOrg: AuthStoreOrg | undefined;
    routeOrgId: bigint;
    navigationCause: 'preload' | 'enter' | 'stay';
}): boolean {
    return hasValidToken && !isTrainingManagerOnly && navigationCause !== 'stay' && hydratedOrg?.id === routeOrgId;
}
